import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import { X, HandCoins } from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";

type ConfirmTakeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
};

export const ConfirmTakeModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: ConfirmTakeModalProps) => {
  const { t } = useTranslations("payments");
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 transform transition-all">
                <div className="flex items-center justify-between px-8 pt-8 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500/10">
                      <HandCoins className="h-6 w-6 text-green-500 dark:text-green-400" />
                    </div>
                    <h2 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">
                      {t("modal_take_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1"
                    aria-label={t("modal_take_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-8 py-2 pb-6">
                  <p className="text-gray-600 dark:text-zinc-400 text-base leading-relaxed">
                    {t("modal_take_description")}
                  </p>
                </div>

                <div className="px-8 py-8 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="min-w-[100px] h-12 px-6 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-white text-base font-bold transition-all hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50"
                  >
                    {t("modal_take_back")}
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="min-w-[180px] h-12 px-8 rounded-full bg-green-500 text-white text-base font-bold transition-all hover:brightness-110 shadow-lg shadow-green-500/20 disabled:opacity-50"
                  >
                    {isLoading ? t("modal_take_processing") : t("modal_take_confirm")}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
